/**
 * Kenik NVR / DVR catalog: KG-NVR IP series plus KG-...UVR / KG-...L 5-in-1
 * hybrid series.
 *
 * All specs verified against kenik.pl product pages via the public
 * WordPress REST API (wp-json/wp/v2/produkt_api/<id>), fetched 2026-07-30.
 * Each entry cites its real kenik.pl product URL. Fields left undefined
 * where the source page did not state a value, per the no-guessing rule
 * (see tools/kenik-recorders-log-2026-07-30.json for per-SKU provenance
 * notes and the one SKU whose detailed spec table was unavailable).
 *
 * Kenik markets two distinct chassis families:
 *   - KG-NVR... : pure IP NVRs (21 SKUs), some with built-in PoE switch
 *     ports (the "-P" suffix models).
 *   - KG-...UVR-A / KG-...L-A : "5w1" hybrid DVRs (10 SKUs) that
 *     auto-detect CVBS/TVI/AHD/CVI on their BNC inputs and additionally
 *     accept a handful of extra IP channels (`hybrid: true`, channel
 *     count = analog inputs + extra IP inputs, as advertised).
 *
 * RAID support is NOT uniform across same-bay-count chassis: only the
 * "R" suffix 8-bay/64-ch models (KG-NVR60864R, KG-NVR60864R-V2) and the
 * 16-bay 128-ch flagship (KG-NVR6016128R) publish a RAID spec row.
 * The plain KG-NVR60832 / KG-NVR60832-V2 (also 8-bay) do not.
 *
 * Built-in PoE is limited to the KG-NVR2014L-P / KG-NVR2014L-P-V2 and
 * KG-NVR2018L-P / KG-NVR2018L-P-V2 models. Every other SKU in this file
 * has `poeInPorts: 0` (no built-in PoE switch).
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const KENIK_NVR_CATALOG: EquipmentCatalogEntry[] = [
  // ── KG-NVR2014L family (4-channel) ──────────────────────────────────
  {
    id: 'kenik-nvr2014l-p',
    manufacturer: 'Kenik',
    model: 'KG-NVR2014L-P',
    category: 'nvr-recorder',
    tagline: '4-channel PoE NVR with built-in 4-port PoE switch, recording up to 5 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2014l-p/',
    specs: {
      channels: 4,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 4,
      inputBandwidthMbps: 30,
      outputBandwidthMbps: 20,
    },
  },
  {
    id: 'kenik-nvr2014l-p-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR2014L-P-V2',
    category: 'nvr-recorder',
    tagline: '4-channel PoE NVR with built-in 4-port PoE switch, recording up to 8 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2014l-p-v2/',
    specs: {
      channels: 4,
      hddBays: 1,
      hddBayMaxTb: 14,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 4,
      poeBudgetW: 45,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
    },
  },
  {
    id: 'kenik-nvr2014l-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR2014L-V2',
    category: 'nvr-recorder',
    tagline: '4-channel NVR without built-in PoE, recording up to 5 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2014l-v2/',
    specs: {
      channels: 4,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 30,
      outputBandwidthMbps: 20,
    },
  },
  {
    id: 'kenik-nvr2014l-v3',
    manufacturer: 'Kenik',
    model: 'KG-NVR2014L-V3',
    category: 'nvr-recorder',
    tagline: '4-channel NVR without built-in PoE, recording up to 8 MP with 14 TB HDD support',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2014l-v3/',
    specs: {
      channels: 4,
      hddBays: 1,
      hddBayMaxTb: 14,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
    },
  },

  // ── KG-NVR2018L family (8-channel) ──────────────────────────────────
  {
    id: 'kenik-nvr2018l-p',
    manufacturer: 'Kenik',
    model: 'KG-NVR2018L-P',
    category: 'nvr-recorder',
    tagline: '8-channel PoE NVR with built-in 8-port PoE switch, recording up to 5 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2018l-p/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 8,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 20,
    },
  },
  {
    id: 'kenik-nvr2018l-p-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR2018L-P-V2',
    category: 'nvr-recorder',
    tagline: '8-channel PoE NVR with built-in 8-port PoE switch, recording up to 8 MP with 4K HDMI',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2018l-p-v2/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 14,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 8,
      poeBudgetW: 45,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
    },
  },
  {
    id: 'kenik-nvr2018l-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR2018L-V2',
    category: 'nvr-recorder',
    tagline: '8-channel NVR without built-in PoE, recording up to 5 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2018l-v2/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 20,
    },
  },
  {
    id: 'kenik-nvr2018l-v3',
    manufacturer: 'Kenik',
    model: 'KG-NVR2018L-V3',
    category: 'nvr-recorder',
    tagline: '8-channel NVR without built-in PoE, recording up to 8 MP with 4K HDMI output',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr2018l-v3/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 14,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
    },
  },

  // ── KG-NVR40xx family ────────────────────────────────────────────────
  {
    id: 'kenik-nvr40116-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR40116-V2',
    category: 'nvr-recorder',
    tagline: '16-channel NVR with 4K HDMI output, recording up to 12 MP with Dual Lens camera support',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr40116-v2/',
    specs: {
      channels: 16,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
    },
  },
  {
    id: 'kenik-nvr4014',
    manufacturer: 'Kenik',
    model: 'KG-NVR4014',
    category: 'nvr-recorder',
    tagline: '4-channel NVR with 4K HDMI output, recording up to 12 MP with onboard IVS analytics',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr4014/',
    specs: {
      channels: 4,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
    },
  },
  {
    id: 'kenik-nvr4018',
    manufacturer: 'Kenik',
    model: 'KG-NVR4018',
    category: 'nvr-recorder',
    tagline: '8-channel NVR with 4K HDMI output, recording up to 8 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr4018/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 10,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
    },
  },
  {
    id: 'kenik-nvr4018-v3',
    manufacturer: 'Kenik',
    model: 'KG-NVR4018-V3',
    category: 'nvr-recorder',
    tagline: '8-channel NVR with 4K HDMI output, recording up to 8 MP with 14 TB HDD support',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr4018-v3/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 14,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
    },
  },
  {
    // NOTE: kenik.pl never published a detailed spec table for this SKU
    // (content.rendered ends after the intro description). Specs below
    // come only from the "Najważniejsze cechy" headline bullets, see
    // tools/kenik-recorders-log-2026-07-30.json for details. Fields with
    // no reliable numeric figure (network ports, alarm I/O counts,
    // form factor) are intentionally left unset.
    id: 'kenik-nvr40216-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR40216-V2',
    category: 'nvr-recorder',
    tagline: '16-channel NVR with 2 HDD bays and alarm I/O, recording up to 8 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr40216-v2/',
    specs: {
      channels: 16,
      hddBays: 2,
      hddBayMaxTb: 10,
      maxResolution: '8 MP',
      codecs: ['H.265+'],
      onvif: true,
      inputBandwidthMbps: 160,
    },
  },

  // ── KG-NVR60xx family (32/64/128-channel) ───────────────────────────
  {
    id: 'kenik-nvr6016128r',
    manufacturer: 'Kenik',
    model: 'KG-NVR6016128R',
    category: 'nvr-recorder',
    tagline: '128-channel enterprise NVR with 16 hot-swap bays, RAID 0/1/5/10 and 8K HDMI output',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr6016128r/',
    specs: {
      channels: 128,
      hddBays: 16,
      hddBayMaxTb: 22,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      raidLevels: ['0', '1', '5', '10'],
      onvif: true,
      networkPorts: '1× RJ-45 2.5G + 2× RJ-45 1G + 2× SFP 1G (RJ45/SFP mutually exclusive)',
      alarmIO: '24 in / 8 out',
      inputBandwidthMbps: 1024,
      outputBandwidthMbps: 800,
    },
  },
  {
    id: 'kenik-nvr60232-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR60232-V2',
    category: 'nvr-recorder',
    tagline: '32-channel NVR with dual HDD bays and 4K HDMI output, recording up to 12 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr60232-v2/',
    specs: {
      channels: 32,
      hddBays: 2,
      hddBayMaxTb: 10,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '8 in / 1 out',
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
    },
  },
  {
    id: 'kenik-nvr60432-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR60432-V2',
    category: 'nvr-recorder',
    tagline: '32-channel NVR with 4 HDD bays and dual HDMI outputs including 4K, recording up to 12 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr60432-v2/',
    specs: {
      channels: 32,
      hddBays: 4,
      hddBayMaxTb: 18,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      networkPorts: '2× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '16 in / 4 out',
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
    },
  },
  {
    id: 'kenik-nvr60832',
    manufacturer: 'Kenik',
    model: 'KG-NVR60832',
    category: 'nvr-recorder',
    tagline: '32-channel NVR with 8 HDD bays and dual HDMI outputs including 4K, recording up to 12 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr60832/',
    specs: {
      channels: 32,
      hddBays: 8,
      hddBayMaxTb: 10,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      networkPorts: '2× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '16 in / 4 out',
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
    },
  },
  {
    id: 'kenik-nvr60832-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR60832-V2',
    category: 'nvr-recorder',
    tagline: '32-channel NVR with 8 HDD bays up to 18 TB each and dual HDMI outputs including 4K',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr60832-v2/',
    specs: {
      channels: 32,
      hddBays: 8,
      hddBayMaxTb: 18,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      networkPorts: '2× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '16 in / 4 out',
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
    },
  },
  {
    id: 'kenik-nvr60864',
    manufacturer: 'Kenik',
    model: 'KG-NVR60864',
    category: 'nvr-recorder',
    tagline: '64-channel NVR with 8 HDD bays, RAID 0/1/5 and dual HDMI outputs including 4K',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr60864/',
    specs: {
      channels: 64,
      hddBays: 8,
      hddBayMaxTb: 10,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.264+'],
      raidLevels: ['0', '1', '5'],
      onvif: true,
      networkPorts: '2× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '16 in / 4 out',
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
    },
  },
  {
    id: 'kenik-nvr60864r',
    manufacturer: 'Kenik',
    model: 'KG-NVR60864R',
    category: 'nvr-recorder',
    tagline: '64-channel NVR with 8 HDD bays, RAID 0/1/5/10 and dual HDMI outputs including 4K',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr60864r/',
    specs: {
      channels: 64,
      hddBays: 8,
      hddBayMaxTb: 10,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      raidLevels: ['0', '1', '5', '10'],
      onvif: true,
      networkPorts: '2× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '16 in / 4 out',
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
    },
  },
  {
    id: 'kenik-nvr60864r-v2',
    manufacturer: 'Kenik',
    model: 'KG-NVR60864R-V2',
    category: 'nvr-recorder',
    tagline: '64-channel NVR with 8 HDD bays up to 22 TB each, RAID 0/1/5/10 and 8K HDMI output',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-nvr60864r-v2/',
    specs: {
      channels: 64,
      hddBays: 8,
      hddBayMaxTb: 22,
      maxResolution: '12 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      raidLevels: ['0', '1', '5', '10'],
      onvif: true,
      networkPorts: '2× RJ-45 10/100/1000/2500 Mbps',
      poeInPorts: 0,
      alarmIO: '16 in / 8 out',
      inputBandwidthMbps: 1024,
      outputBandwidthMbps: 800,
    },
  },

  // ── 5-in-1 hybrid DVRs (CVBS/TVI/AHD/CVI auto-detect + extra IP) ────
  {
    id: 'kenik-7314uvr-a2',
    manufacturer: 'Kenik',
    model: 'KG-7314UVR-A2',
    category: 'nvr-recorder',
    tagline: '4+4 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording up to 8 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-7314uvr-a2/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '4 in / 1 out',
      hybrid: true,
    },
  },
  {
    id: 'kenik-7318uvr-a2',
    manufacturer: 'Kenik',
    model: 'KG-7318UVR-A2',
    category: 'nvr-recorder',
    tagline: '8+8 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording up to 8 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-7318uvr-a2/',
    specs: {
      channels: 16,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '4 in / 1 out',
      hybrid: true,
    },
  },
  {
    id: 'kenik-73216uvr-a2',
    manufacturer: 'Kenik',
    model: 'KG-73216UVR-A2',
    category: 'nvr-recorder',
    tagline: '16+4 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording up to 8 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-73216uvr-a2/',
    specs: {
      channels: 20,
      hddBays: 2,
      hddBayMaxTb: 8,
      maxResolution: '8 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '16 in / 4 out',
      hybrid: true,
    },
  },
  {
    id: 'kenik-53116uvr-a',
    manufacturer: 'Kenik',
    model: 'KG-53116UVR-A',
    category: 'nvr-recorder',
    tagline: '16+4 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording up to 5 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-53116uvr-a/',
    specs: {
      channels: 20,
      hddBays: 1,
      // Headline bullet says "do 10TB", the technical spec table says
      // "do 8TB każdy HDD", the two disagree. Using the spec-table value
      // as the more authoritative figure; see provenance log.
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      hybrid: true,
    },
  },
  {
    id: 'kenik-5314uvr-a',
    manufacturer: 'Kenik',
    model: 'KG-5314UVR-A',
    category: 'nvr-recorder',
    tagline: '4+2 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording up to 5 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-5314uvr-a/',
    specs: {
      channels: 6,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      hybrid: true,
    },
  },
  {
    id: 'kenik-5318uvr-a',
    manufacturer: 'Kenik',
    model: 'KG-5318UVR-A',
    category: 'nvr-recorder',
    tagline: '8+2 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording up to 5 MP',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-5318uvr-a/',
    specs: {
      channels: 10,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      hybrid: true,
    },
  },
  {
    id: 'kenik-53216uvr-a',
    manufacturer: 'Kenik',
    model: 'KG-53216UVR-A',
    category: 'nvr-recorder',
    tagline: '16+4 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, with human/vehicle SMD analytics',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-53216uvr-a/',
    specs: {
      channels: 20,
      hddBays: 2,
      hddBayMaxTb: 8,
      maxResolution: '5 MP',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100/1000 Mbps',
      poeInPorts: 0,
      alarmIO: '4 in / 1 out',
      hybrid: true,
    },
  },
  {
    id: 'kenik-13516l-a',
    manufacturer: 'Kenik',
    model: 'KG-13516L-A',
    category: 'nvr-recorder',
    tagline: '16+2 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording at 1080P Lite',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-13516l-a/',
    specs: {
      channels: 18,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '1080P',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      hybrid: true,
    },
  },
  {
    id: 'kenik-1354l-a2',
    manufacturer: 'Kenik',
    model: 'KG-1354L-A2',
    category: 'nvr-recorder',
    tagline: '4+2 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording at 1080P Lite',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-1354l-a2/',
    specs: {
      channels: 6,
      hddBays: 1,
      // Headline bullet says "do 10TB", the technical spec table says
      // "Do 8TB", the two disagree. Using the spec-table value as the
      // more authoritative figure; see provenance log.
      hddBayMaxTb: 8,
      maxResolution: '1080P',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      hybrid: true,
    },
  },
  {
    id: 'kenik-1358l-a2',
    manufacturer: 'Kenik',
    model: 'KG-1358L-A2',
    category: 'nvr-recorder',
    tagline: '8+2 hybrid 5-in-1 DVR accepting CVBS/TVI/AHD/CVI plus IP input, recording at 1080P Lite',
    verified: true,
    datasheetUrl: 'https://kenik.pl/produkt/kg-1358l-a2/',
    specs: {
      channels: 10,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '1080P',
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× RJ-45 10/100 Mbps',
      poeInPorts: 0,
      hybrid: true,
    },
  },
];
